import api from './api'

export interface Lead {
  id: number
  user_id: number
  telegram_user_id: number
  username: string | null
  channel_id: number | null
  rule_id: number | null
  rule_name: string | null
  message_text: string
  need_description: string | null
  confidence: number | null
  status: string
  created_at: string
  contacted_at: string | null
}

export interface LeadDetail extends Lead {
  message: Message | null
  channel_title: string | null
}

export interface Message {
  id: number
  lead_id: number
  generated_text: string
  edited_text: string | null
  sent_at: string | null
  read_at: string | null
  replied: boolean
  send_status: string  // draft|queued|sending|sent|failed|paused
  retry_count: number
  error_text: string | null
  created_at: string
}

export interface SendStatusItem {
  message_id: number
  send_status: string
  error_text: string | null
  sent_at: string | null
  retry_count: number
}

export interface SendQueueMessage {
  id: number
  lead_id: number
  lead_username: string | null
  send_status: string
  created_at: string
  sent_at: string | null
  error_text: string | null
}

export interface SendQueueResponse {
  total_queued: number
  total_sending: number
  total_sent: number
  total_failed: number
  total_paused: number
  is_paused: boolean
  messages: SendQueueMessage[]
}

export interface SuggestReplyResponse {
  reply_text: string
  tokens_used: number
  message_id: number
}

export interface LeadStats {
  total_leads: number
  pending_review: number
  approved: number
  rejected: number
  message_generated: number
  sent: number
  replied: number
  today_leads: number
}

export interface LeadsFilter {
  status?: string
  channel_id?: number
  rule_id?: number
  date_from?: string
  date_to?: string
  limit?: number
  offset?: number
}

export const leadsService = {
  async getLeads(filters?: LeadsFilter): Promise<Lead[]> {
    const response = await api.get<Lead[]>('/leads', { params: filters })
    return response.data
  },

  async getLead(leadId: number): Promise<LeadDetail> {
    const response = await api.get<LeadDetail>(`/leads/${leadId}`)
    return response.data
  },

  async getStats(): Promise<LeadStats> {
    const response = await api.get<LeadStats>('/leads/stats')
    return response.data
  },

  async approveLead(leadId: number): Promise<Lead> {
    const response = await api.patch<Lead>(`/leads/${leadId}/approve`)
    return response.data
  },

  async rejectLead(leadId: number): Promise<Lead> {
    const response = await api.patch<Lead>(`/leads/${leadId}/reject`)
    return response.data
  },

  async generateMessages(
    leadIds: number[],
    userOffering: string,
    tone: string = 'friendly_professional',
    language: string = 'ru'
  ): Promise<{ message: string }> {
    const response = await api.post<{ message: string }>(
      '/leads/generate-messages',
      { lead_ids: leadIds },
      {
        params: {
          user_offering: userOffering,
          tone,
          language,
        },
      }
    )
    return response.data
  },

  async getMessage(messageId: number): Promise<Message> {
    const response = await api.get<Message>(`/leads/messages/${messageId}`)
    return response.data
  },

  async updateMessage(messageId: number, editedText: string): Promise<Message> {
    const response = await api.patch<Message>(`/leads/messages/${messageId}`, {
      edited_text: editedText,
    })
    return response.data
  },

  async sendMessages(messageIds: number[]): Promise<{ message: string }> {
    const response = await api.post<{ message: string }>('/leads/messages/send', {
      message_ids: messageIds,
    })
    return response.data
  },

  // Send queue & status
  async getSendStatus(messageIds: number[]): Promise<SendStatusItem[]> {
    const response = await api.get<SendStatusItem[]>('/leads/send-status', {
      params: { message_ids: messageIds.join(',') },
    })
    return response.data
  },

  async getSendQueue(): Promise<SendQueueResponse> {
    const response = await api.get<SendQueueResponse>('/leads/send-queue')
    return response.data
  },

  async pauseSending(): Promise<{ message: string }> {
    const response = await api.post<{ message: string }>('/leads/send-queue/pause')
    return response.data
  },

  async resumeSending(): Promise<{ message: string }> {
    const response = await api.post<{ message: string }>('/leads/send-queue/resume')
    return response.data
  },

  async cancelSending(): Promise<{ message: string }> {
    const response = await api.post<{ message: string }>('/leads/send-queue/cancel')
    return response.data
  },

  // Reply suggestions
  async suggestReply(
    leadId: number,
    data: { custom_prompt?: string; language?: string }
  ): Promise<SuggestReplyResponse> {
    const response = await api.post<SuggestReplyResponse>(
      `/leads/${leadId}/suggest-reply`,
      data
    )
    return response.data
  },

  async sendReply(
    leadId: number,
    data: { reply_text: string }
  ): Promise<{ message: string }> {
    const response = await api.post<{ message: string }>(
      `/leads/${leadId}/send-reply`,
      data
    )
    return response.data
  },

  // Bulk actions
  async bulkAction(
    leadIds: number[],
    action: string
  ): Promise<{ message: string }> {
    const response = await api.post<{ message: string }>('/leads/bulk-action', {
      lead_ids: leadIds,
      action,
    })
    return response.data
  },
}
